import React, { useState } from 'react';
import { FormEvent } from 'react';
import { ArrowRightIcon, SearchIcon } from 'lucide-react';
import { CommunityPharmacyExplorer } from '../components/CommunityPharmacyExplorer';
import { HowItWorksSection } from '../components/HowItWorksSection';
import { LiveActivityCounter } from '../components/LiveActivityCounter';
type HomePageProps = {
  onSearch: (query: string) => void;
  onOpenMerchant: () => void;
};
const heroImageUrl = 'https://cdn.magicpatterns.com/patterns/generated-images/950561a3-b3e0-4806-8ad6-2dddac133dd1.jpg';
export function HomePage({
  onSearch,
  onOpenMerchant
}: HomePageProps) {
  const [query, setQuery] = useState('');
  const [location, setLocation] = useState('');
  const [error, setError] = useState('');
  function startSearch() {
    if (!query.trim() && !location.trim()) {
      setError('Enter a pharmacy name or location to begin.');
      return;
    }
    setError('');
    onSearch(`${query.trim()} ${location.trim()}`.trim());
  }
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    startSearch();
  }
  return <main className="min-h-[calc(100vh-4rem)] bg-white" data-id="element-231" data-mp="2epyk5k9l55xh">
      <section className="relative w-full overflow-hidden bg-[#FFF9EA] py-16 sm:py-24 lg:py-32" data-id="element-232" data-mp="2eone1dso3m37">
        <div className="mx-auto max-w-7xl px-6 md:px-12 lg:px-16" data-id="element-233" data-mp="2jhynlmov6gyt">
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-8" data-id="element-234" data-mp="2of8cn18ccqm4">
            <div className="max-w-2xl text-center lg:text-left" data-id="element-235" data-mp="1rqsissy77v37">
              <h1 className="text-[38px] font-black leading-[1.1] tracking-tight text-[#1C1C1C] sm:text-5xl lg:text-6xl" data-id="element-236" data-mp="1vrmazyiobw1w">
                Find a pharmacy that meets your needs
              </h1>
              <p className="mt-6 text-lg leading-8 text-[#4D4D4D]" data-id="element-237" data-mp="25psa68d3io32">
                On a general review site, your pharmacy review gets buried next to
                a nail salon and a dentist. Here, it&apos;s the only thing that
                matters — rated by stock confidence, wait time, and empathetic
                care.
              </p>

              <div className="mt-10" data-id="element-238" data-mp="2rfi9eihy9efz">
                <form onSubmit={submit} className="w-full max-w-xl mx-auto lg:mx-0" noValidate data-id="element-239" data-mp="2rimle21npeh7">
                  <label htmlFor="home-search" className="sr-only" data-id="element-240" data-mp="2mk2xvx7ezumq">
                    Search by pharmacy name or ZIP code
                  </label>
                  <div className="flex flex-col sm:flex-row overflow-hidden rounded-2xl sm:rounded-full border border-gray-200 bg-white shadow-lg focus-within:border-[#008F82] focus-within:ring-2 focus-within:ring-[#008F82]/20" data-id="element-241" data-mp="2wgxuubo1hmyw">
                    <input id="home-search" value={query} onChange={event => {
                    setQuery(event.target.value);
                    setError('');
                  }} className="min-w-0 flex-[2] px-5 py-4 text-base text-gray-900 outline-none placeholder:text-gray-500" placeholder="Pharmacy name or condition" aria-describedby={error ? 'home-search-error' : undefined} data-id="element-242" data-mp="23t221i1lcf7e" />
                    <div className="hidden sm:block w-0.5 bg-gray-200 my-3" data-id="element-243" data-mp="59xah6t1so71" />
                    <div className="sm:hidden h-px bg-gray-200 mx-4" data-id="element-244" data-mp="3mpx0kwfyifhz" />
                    <input id="home-location" value={location} onChange={event => {
                    setLocation(event.target.value);
                    setError('');
                  }} className="min-w-0 flex-1 px-5 py-4 text-base text-gray-900 outline-none placeholder:text-gray-500" placeholder="City, State or ZIP" autoComplete="postal-code" data-id="element-245" data-mp="3mrq628eu2toe" />
                    <button type="submit" className="shrink-0 bg-[#E28743] px-8 py-4 text-base font-bold text-white hover:bg-[#D07635] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E28743] focus-visible:ring-offset-2" aria-label="Search" data-id="element-246" data-mp="eby4zvwwcw50">
                      Find care
                    </button>
                  </div>
                  {error && <p id="home-search-error" className="mt-3 text-left text-sm font-semibold text-[#A63A2B]" aria-live="polite" data-id="element-247" data-mp="35i73tqopytqn">
                      {error}
                    </p>}
                </form>
              </div>
            </div>
            <div className="relative hidden lg:block" data-id="element-248" data-mp="3o7vkcertq7mh">
              <img src="https://cdn.magicpatterns.com/patterns/generated-images/b9b434f5-dadd-4f86-a2c0-5332933b5416.jpg" alt="Person using pharmacy app" className="w-full object-contain mix-blend-multiply" data-id="element-249" data-mp="3o6kjontodfzl" />
            </div>
          </div>
        </div>
      </section>

      <LiveActivityCounter data-id="element-250" data-mp="o9ci8uk8mkzf" />
      <CommunityPharmacyExplorer data-id="element-251" data-mp="oa5fn4xb3y4w" />
      <HowItWorksSection data-id="element-252" data-mp="oaxdyniag9yv" />

      <div className="mx-auto w-full px-6 md:px-12 lg:px-16" data-id="element-253" data-mp="o7ja1atz7spb">
        <section className="my-8 rounded-2xl bg-[#3D1952] px-4 py-16 text-center text-white sm:px-6" data-id="element-254" data-mp="sbh95ljax3id">
          <div className="mx-auto max-w-2xl space-y-6" data-id="element-255" data-mp="x8hh61zpogc4">
            <span className="text-xs font-medium uppercase tracking-widest text-white/80" data-id="element-256" data-mp="121al4nncvkn5">
              For Pharmacy Owners
            </span>
            <h2 className="text-2xl font-bold leading-tight tracking-tight md:text-3xl" data-id="element-257" data-mp="16zc0oylkp1tv">
              Your patients are already talking.{' '}
              <br className="hidden md:inline" data-id="element-258" data-mp="1g0s2ihybnvmh" />
              The difference is we&apos;re keeping score.
            </h2>
            <p className="mx-auto max-w-xl text-sm font-normal leading-relaxed text-white/90 md:text-base" data-id="element-259" data-mp="1kzv946tya9wk">
              Not a general review buried in a star rating — a separate score
              for stock confidence, wait time, and empathetic care. So you know
              exactly what&apos;s working and what to fix.
            </p>
            <div className="pt-4" data-id="element-260" data-mp="twrjzgokfgq4">
              <button type="button" onClick={onOpenMerchant} className="rounded-full bg-[#E28743] px-8 py-3.5 text-sm font-semibold text-white shadow-sm hover:bg-[#D07635] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#3D1952]" data-id="element-261" data-mp="oxoho3zyzolu">
                Claim your pharmacy free
              </button>
            </div>
          </div>
        </section>

        <section className="rounded-2xl bg-[#F7F7F7] px-4 py-12 sm:px-6" data-id="element-262" data-mp="1lnnysmu6rvg1">
          <div className="mx-auto max-w-4xl space-y-8" data-id="element-263" data-mp="1lps1x2k9gqf9">
            <div className="text-center" data-id="element-264" data-mp="1hlu54yierrpd">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#008F82]" data-id="element-265" data-mp="1ho6sxsaukwac">
                Real Experiences, Real People
              </span>
              <h2 className="mt-1 text-xl font-bold text-gray-900" data-id="element-266" data-mp="1qntj1hy4mfo3">
                What your neighbors actually said
              </h2>
            </div>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2" data-id="element-267" data-mp="2f7n6np5saydx">
              <article className="space-y-3 rounded-xl border border-gray-100 bg-white p-5" data-id="element-268" data-mp="2f6vmdahn0n2y">
                <div className="flex items-start justify-between gap-3" data-id="element-269" data-mp="209uriy45gc09">
                  <div data-id="element-270" data-mp="258fu346vj7rv">
                    <h3 className="text-sm font-bold text-gray-900" data-id="element-271" data-mp="257wbrmdbahdb">
                      Maria G.
                    </h3>
                    <p className="text-xs text-gray-400" data-id="element-272" data-mp="1rg80tmestyxx">
                      Refill pickup • 3 days ago
                    </p>
                  </div>
                  <span className="text-xs font-semibold text-[#008F82]" data-id="element-273" data-mp="1hkxx8u8r5ji1">
                    Care 5/5
                  </span>
                </div>
                <p className="text-sm leading-relaxed text-gray-600" data-id="element-274" data-mp="1mgmyr34c1agz">
                  Staff remembered my name and had my medication ready before I
                  even checked in. Never had that at a chain.
                </p>
              </article>
              <article className="space-y-3 rounded-xl border border-gray-100 bg-white p-5" data-id="element-275" data-mp="1syzx6mcb3ash">
                <div className="flex items-start justify-between gap-3" data-id="element-276" data-mp="1o29dzq57fz2u">
                  <div data-id="element-277" data-mp="1j1lqvfk3k2dw">
                    <h3 className="text-sm font-bold text-gray-900" data-id="element-278" data-mp="1j13jbmrzi1qo">
                      James T.
                    </h3>
                    <p className="text-xs text-gray-400" data-id="element-279" data-mp="1e2se5pcmwfes">
                      New prescription • 1 week ago
                    </p>
                  </div>
                  <span className="text-xs font-semibold text-amber-600" data-id="element-280" data-mp="15agmk05salx8">
                    Wait 2/5
                  </span>
                </div>
                <p className="text-sm leading-relaxed text-gray-600" data-id="element-281" data-mp="109j7hwg2m7zy">
                  Long wait and they were out of one of my medications. Had to
                  come back the next day.
                </p>
              </article>
            </div>
          </div>
        </section>
      </div>
    </main>;
}